import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable, PLATFORM_ID } from '@angular/core'
import { signIn, signUp, resetPassword, resendSignUpCode, confirmSignUp, confirmSignIn, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth'
import { Observable } from 'rxjs/internal/Observable'
import { Router } from '@angular/router'
import { jwtDecode } from 'jwt-decode'
import { forgotPassResetmailApi, mainDomain, resetPassMailApi, userProfileUrl } from '../common/utilities/constants'
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { LoaderService } from './loader.service'
import { StateService } from '../common/services/state.service'

import { environment } from 'src/environments/environment'
import { Hub } from 'aws-amplify/utils'
import { DOCUMENT } from '@angular/common'
@Injectable({
  providedIn: 'root'
})
export class UserAuthService {
  verifyUser: boolean = false
  cognitUser: any = { }
  userInfo: any = {}
  userId: string = ''
  isIdpUser: boolean = false
  pendingNewPasswordChallenge: boolean = false
  headerData = new HttpHeaders({
    'Content-Type': 'application/json'
  })
  private removeListener?: () => void
  constructor (private http: HttpClient, private router: Router, private loaderService: LoaderService, private stateService: StateService,
    @Inject(PLATFORM_ID) public platformId: Object, @Inject(DOCUMENT) public document: any) { }

  cognitUserSubject = new BehaviorSubject<any | null>(null)
  cognitUserInfo = this.cognitUserSubject.asObservable()

  setCognitUser (user: any) {
    this.cognitUserSubject.next(user)
  }
  getCognitUser (): any | null {
    return this.cognitUserSubject.value
  }
  setUserId (data: any) {
    this.userId = data
  }
  getUserId () {
    return this.userId
  }
  setVerifyUser (data: any) {
    this.verifyUser = data
  }
  getVerifyUser () {
    return this.verifyUser
  }
  setUserInfo (data: any) {
    this.userInfo = data
  }
  getUserInfo () {
    return this.userInfo
  }
  setIdpUser (data: any) {
    this.isIdpUser = data
  }
  getIdpUser () {
    return this.isIdpUser
  }
  setPendingNewPasswordChallenge (data: boolean) {
    this.pendingNewPasswordChallenge = data
  }
  getPendingNewPasswordChallenge () {
    return this.pendingNewPasswordChallenge
  }
  signUp (user: any): Promise<any> {
    return signUp(user)
  }
  signIn (signInInput: any): Promise<any> {
    return signIn(signInInput)
  }
  confirmSignIn (challengeResponse: any): Promise<any> {
    return confirmSignIn(challengeResponse)
  }
  resetPassword (username: any): Promise<any> {
    return resetPassword(username)
  }
  confirmUserSignUp (otpInfo: any): Promise<any> {
    return confirmSignUp(otpInfo)
  }
  resendSignUpCode (otpInfo: any): Promise<any> {
    return resendSignUpCode(otpInfo)
  }
  createUserProfile (userData: any): Observable<any> {
    return this.http.post<any>(userProfileUrl, userData, { headers: this.headerData })
  }
  getUserDetails () {
    return this.http.get<any>(userProfileUrl, { headers: this.headerData })
  }
  acknowledgeForgotPasswordReset (userData: any) {
    const url = forgotPassResetmailApi
    return this.http.post<any>(url, userData, { headers: this.headerData })
  }
  acknowledgePasswordReset (userData: any) {
    const url = resetPassMailApi
    return this.http.post<any>(url, userData, { headers: this.headerData })
  }
  activateRouteAfterTimedOut (url: any) {
    setTimeout(() => {
      this.router.navigate([url])
    }, 1500)
  }
  isSessionExpired (token: any) {
    if (token) {
      const decodedToken: any = jwtDecode(token)
      const currentTime = Math.floor(Date.now() / 1000)
      if (decodedToken.exp && decodedToken.exp < currentTime) {
        return true
      }
    }
    return false
  }
  setCookie (name: string, value: string) {
    const isLocalhost = window.location.hostname === 'localhost'
    const secureFlag = isLocalhost ? '' : 'Secure;'
    const sameSite = isLocalhost ? 'lax' : 'Strict'
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; domain=${environment.mainDomain}; SameSite=${sameSite}; ${secureFlag}`
  }
  deleteCookie (name: string) {
    const isLocalhost = window.location.hostname === 'localhost'
    const domain = isLocalhost ? '' : `domain=${mainDomain};`
    const sameSite = isLocalhost ? 'Lax' : 'Strict'
    const secure = isLocalhost ? '' : 'Secure;'
    document.cookie = `${name}=; path=/; ${domain} Max-Age=0; SameSite=${sameSite}; ${secure}`
  }
  deleteCognitoCookies () {
    const cookies = document.cookie.split(';')
    cookies.forEach(cookie => {
     const name = cookie.split('=')[0].trim()
      if (name.startsWith('CognitoIdentityServiceProvider')) {
        this.deleteCookie(name)
      }
    })
  }
  getCookie (name: string): string | null {
    const cookies = document.cookie.split('; ')
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split('=')
      if (cookieName === name) {
        return decodeURIComponent(cookieValue)
      }
    }
    return null
  }
  async isIeltsAccountExist () {
    this.loaderService.show()
    const session = await fetchAuthSession()
    const isSessionAvailable = session?.tokens?.idToken || ''
    if (isSessionAvailable) {
      const userProfileInfo = await firstValueFrom(this.getUserDetails())
      if (userProfileInfo?.body?.items?.length) {
        this.stateService.userDetails.next(userProfileInfo?.body?.items[0])
      }
      this.setIdpUser(userProfileInfo?.body?.items?.length === 0)
      this.loaderService.hide()
      return userProfileInfo?.body?.items?.length === 0
    }
    this.loaderService.hide()
    return false
  }
  async getAuthorisedUserMail () {
    try {
      const userInfo = await fetchUserAttributes()
      this.setUserId(userInfo?.email)
      return userInfo?.email
    } catch (error) {
      return ''
    }
  }
  redirectTo () {
    const referrerUrl = decodeURIComponent(this.getCookie('referrerUrl') || '')
    this.deleteCookie('referrerUrl')
    this.deleteCookie('previousPageUrl')
    sessionStorage.removeItem('bookingType')
    const documentReferrer = document.referrer || ''

    if (referrerUrl) {
      window.location.replace(referrerUrl)
    } else if (documentReferrer.includes('my-tests#')) {
                  window.location.replace(documentReferrer)
                } else {
      this.router.navigate(['/dashboard'])
    }
  }
  syncCognitoSession (): void {
    this.removeListener = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
          await this.syncCookiesFromSession()
          break
        case 'signedOut':
          this.deleteCookie('isLoggedIn')
          break
      }
    })
  }
  async syncCookiesFromSession () {
    const session = await fetchAuthSession()
    if (session?.tokens?.idToken) {
      this.setCookie('isLoggedIn', 'true')
    } else {
      this.deleteCookie('isLoggedIn')
    }
  }

  revokeUser (userProfileId: string) {
    const url = environment.rtbfRevokeApi.replace('{userProfileId}', userProfileId)
    return this.http.put(url, null, { headers: this.headerData })
  }
  deleteUser (userProfileId: string) {
    const url = environment.rtbfApi.replace('{userProfileId}', userProfileId)
    return this.http.delete(url, {
      headers: this.headerData,
      body: { country: 'Australia' }
    })
  }

  getUserStatus (userProfileId: string) {
    const url = environment.rtbfStatusApi.replace('{userprofileID}', userProfileId)
    return this.http.get(url, { headers: this.headerData })
  }

  ngOnDestroy (): void {
    this.removeListener?.()
  }
}
